import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  Building,
  Plus, 
  Trash2, 
  Edit3,
  MapPin,
  Eye,
  Loader2,
  Hash,
  FileText,
  Search,
  Filter,
  Package,
  Gauge
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Building type options
const BUILDING_TYPES = [
  { value: 'process', label: 'Process Building' },
  { value: 'storage', label: 'Storage Facility' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'office', label: 'Office Building' },
  { value: 'control_room', label: 'Control Room' },
  { value: 'utility', label: 'Utility Building' },
  { value: 'maintenance', label: 'Maintenance Shop' },
  { value: 'substation', label: 'Electrical Substation' },
  { value: 'compressor_house', label: 'Compressor House' },
  { value: 'pump_house', label: 'Pump House' },
  { value: 'other', label: 'Other' },
];

export function BuildingsPanel({ 
  isOpen,
  onClose,
  currentSweep,
  cameraPosition,
  onNavigateToBuilding
}) {
  const { token, canEdit, isViewer } = useAuth();
  const { currentTour } = useTour();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    building_type: '',
    description: '',
    building_number: '',
    area: '',
    floors: '',
    capturePosition: true,
  });

  // Fetch buildings
  const fetchBuildings = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchBuildings();
    }
  }, [isOpen, fetchBuildings]);

  // Filter buildings
  const filteredBuildings = buildings.filter(building => {
    const matchesSearch = building.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         building.building_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || building.building_type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !currentTour?.id) return;

    try {
      const url = editingBuilding 
        ? `${API_URL}/api/buildings/${editingBuilding.id}`
        : `${API_URL}/api/buildings`;
      
      const method = editingBuilding ? 'PUT' : 'POST';
      
      const submitData = {
        ...formData,
        tour_id: currentTour.id,
      };

      // Only include position and rotation if capturePosition is enabled
      if (formData.capturePosition && currentSweep?.id) {
        submitData.sweep_id = currentSweep.id;
        if (cameraPosition?.position) {
          submitData.position = cameraPosition.position;
        }
        if (cameraPosition?.rotation) {
          submitData.rotation = cameraPosition.rotation;
        }
      }

      // Remove capturePosition from submission (frontend-only)
      delete submitData.capturePosition;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        await fetchBuildings();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Error saving building:', error);
    }
  };

  // Handle delete
  const handleDelete = async (buildingId) => {
    if (!token || !window.confirm('Are you sure you want to delete this building?')) return;

    try {
      const response = await fetch(`${API_URL}/api/buildings/${buildingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchBuildings();
      }
    } catch (error) {
      console.error('Error deleting building:', error);
    }
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingBuilding(null);
    setFormData({
      name: '',
      building_type: '',
      description: '',
      building_number: '',
      area: '',
      floors: '',
      capturePosition: true,
    });
  };

  // Handle edit
  const handleEdit = (building) => {
    setEditingBuilding(building);
    setFormData({
      name: building.name || '',
      building_type: building.building_type || '',
      description: building.description || '',
      building_number: building.building_number || '',
      area: building.area || '',
      floors: building.floors || '',
      capturePosition: false, // Don't capture new position when editing
    });
    setIsCreateDialogOpen(true);
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px] p-0">
        <SheetHeader className="p-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-primary" />
            <SheetTitle>Buildings</SheetTitle>
          </div>
          <SheetDescription>
            Manage buildings and structures within this tour
          </SheetDescription>
        </SheetHeader>

        <div className="p-4 border-b space-y-3">
          {/* Search and Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search buildings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {BUILDING_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Add Button */}
          {canEdit && (
            <Button 
              onClick={() => setIsCreateDialogOpen(true)} 
              className="w-full gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Building
            </Button>
          )}
        </div>

        {/* Buildings List */}
        <ScrollArea className="h-[calc(100vh-280px)]">
          <div className="p-4 space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredBuildings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No buildings found</p>
                {canEdit && (
                  <p className="text-sm mt-1">Click "Add Building" to create one</p>
                )}
              </div>
            ) : (
              filteredBuildings.map(building => (
                <div 
                  key={building.id}
                  className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium truncate">{building.name}</h4>
                        {building.building_type && (
                          <Badge variant="outline" className="text-xs">
                            {BUILDING_TYPES.find(t => t.value === building.building_type)?.label || building.building_type}
                          </Badge>
                        )}
                      </div>
                      {building.building_number && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Hash className="h-3 w-3" />
                          {building.building_number}
                        </p>
                      )}
                      {building.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {building.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        {building.area && (
                          <span>{building.area} sq ft</span>
                        )}
                        {building.floors && (
                          <span>{building.floors} floors</span>
                        )}
                      </div>
                      {building.sweep_id && (
                        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          Position saved
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {building.sweep_id && onNavigateToBuilding && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => onNavigateToBuilding(building.sweep_id, {
                            rotation: building.rotation,
                            transitionTime: 1500
                          })}
                          title="Go to location"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {canEdit && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEdit(building)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(building.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingBuilding ? 'Edit Building' : 'Add Building'}
              </DialogTitle>
              <DialogDescription>
                {editingBuilding ? 'Update building details' : 'Add a new building to this tour'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Building Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Main Process Building"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="building_type">Type</Label>
                  <Select 
                    value={formData.building_type} 
                    onValueChange={(value) => setFormData({...formData, building_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUILDING_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="building_number">Building Number</Label>
                  <Input
                    id="building_number"
                    value={formData.building_number}
                    onChange={(e) => setFormData({...formData, building_number: e.target.value})}
                    placeholder="e.g., B-001"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="area">Area (sq ft)</Label>
                  <Input
                    id="area"
                    value={formData.area}
                    onChange={(e) => setFormData({...formData, area: e.target.value})}
                    placeholder="e.g., 5000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="floors">Number of Floors</Label>
                  <Input
                    id="floors"
                    value={formData.floors}
                    onChange={(e) => setFormData({...formData, floors: e.target.value})}
                    placeholder="e.g., 2"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Building description..."
                  rows={3}
                />
              </div>

              {/* Link to 3D Position */}
              {!editingBuilding && (
                <div className="p-3 rounded-lg border space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      Link to 3D Position
                    </Label>
                    <Button
                      type="button"
                      variant={formData.capturePosition ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, capturePosition: !prev.capturePosition }))}
                    >
                      {formData.capturePosition ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                  {formData.capturePosition && currentSweep?.id && (
                    <p className="text-xs text-green-600">
                      ✓ Current position will be saved
                    </p>
                  )}
                  {formData.capturePosition && !currentSweep?.id && (
                    <p className="text-xs text-yellow-600">
                      ⚠️ Navigate in the 3D viewer to capture position
                    </p>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingBuilding ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

export default BuildingsPanel;
